import { eq, inArray, isNull, sql } from "drizzle-orm";
import { makePgArray } from "drizzle-orm/pg-core";

import { createLogger } from "@/logger";

const log = createLogger("reset");

import { db } from "@/db/db.ts";
import {
  accountEvent,
  audioEncoding,
  callRecord,
  displayArtifact,
  feed,
  file,
  item,
  link,
  pipelineArtifactMetadata,
  transcription,
} from "@/db/schema.ts";
import { emitTableUpdates } from "@/models/eventing/TableEventManager";

export type DeletionResult = {
  deletedItems: number;
  deletedContentIds: number;
  affectedFeeds: number;
};

export async function deleteItemsAndOrphanedContent(
  itemIds: string[],
  feedIds: string[],
  label: string,
): Promise<DeletionResult> {
  log.debug(`[${label}] Deleting ${itemIds.length} items from ${feedIds.length} feeds`);

  const deletedItemIdsCte = db
    .$with("deleted_item_ids", { id: item.id })
    .as(sql`SELECT unnest(${sql.placeholder("itemIds")}::text[]) as "id"`);

  const deletedContentIdsCte = db
    .$with("deleted_content_ids")
    .as(
      db
        .selectDistinct({ contentId: item.contentId })
        .from(item)
        .innerJoin(deletedItemIdsCte, eq(item.id, deletedItemIdsCte.id)),
    );

  const stillReferencedCte = db
    .$with("still_referenced")
    .as(
      db
        .selectDistinct({ contentId: item.contentId })
        .from(item)
        .innerJoin(deletedContentIdsCte, eq(item.contentId, deletedContentIdsCte.contentId))
        .leftJoin(deletedItemIdsCte, eq(item.id, deletedItemIdsCte.id))
        .where(isNull(deletedItemIdsCte.id)),
    );

  const orphanedRows = await db
    .with(deletedItemIdsCte, deletedContentIdsCte, stillReferencedCte)
    .select({ contentId: deletedContentIdsCte.contentId })
    .from(deletedContentIdsCte)
    .leftJoin(stillReferencedCte, eq(deletedContentIdsCte.contentId, stillReferencedCte.contentId))
    .where(isNull(stillReferencedCte.contentId))
    .execute({ itemIds: makePgArray(itemIds) });

  const orphanedContentIds = orphanedRows.map((r) => r.contentId);

  const itemIdsSub = db
    .$with("item_ids_sub", { id: item.id })
    .as(sql`SELECT unnest(${sql.placeholder("itemIds")}::text[]) as "id"`);

  const itemIdsSubquery = db.with(itemIdsSub).select({ id: itemIdsSub.id }).from(itemIdsSub);
  const itemIdParams = { itemIds: makePgArray(itemIds) };

  await db.delete(item).where(inArray(item.id, itemIdsSubquery)).execute(itemIdParams);
  await db.delete(accountEvent).where(inArray(accountEvent.itemId, itemIdsSubquery)).execute(itemIdParams);

  if (orphanedContentIds.length > 0) {
    log.debug(`[${label}] Deleting ${orphanedContentIds.length} orphaned content records`);

    const contentIdsSub = db
      .$with("content_ids_sub", { contentId: transcription.contentId })
      .as(sql`SELECT unnest(${sql.placeholder("contentIds")}::text[]) as "contentId"`);

    const contentIdsSubquery = db
      .with(contentIdsSub)
      .select({ contentId: contentIdsSub.contentId })
      .from(contentIdsSub);
    const contentIdParams = { contentIds: makePgArray(orphanedContentIds) };

    await db.delete(transcription).where(inArray(transcription.contentId, contentIdsSubquery)).execute(contentIdParams);
    await db.delete(audioEncoding).where(inArray(audioEncoding.contentId, contentIdsSubquery)).execute(contentIdParams);
    await db
      .delete(displayArtifact)
      .where(inArray(displayArtifact.contentId, contentIdsSubquery))
      .execute(contentIdParams);
    await db.delete(file).where(inArray(file.contentId, contentIdsSubquery)).execute(contentIdParams);
    await db.delete(link).where(inArray(link.contentId, contentIdsSubquery)).execute(contentIdParams);
    await db
      .delete(pipelineArtifactMetadata)
      .where(inArray(pipelineArtifactMetadata.contentId, contentIdsSubquery))
      .execute(contentIdParams);
    await db.delete(callRecord).where(inArray(callRecord.contentId, contentIdsSubquery)).execute(contentIdParams);
  }

  const feedIdsSub = db
    .$with("feed_ids_sub", { id: feed.id })
    .as(sql`SELECT unnest(${sql.placeholder("feedIds")}::text[]) as "id"`);

  const feedIdsSubquery = db.with(feedIdsSub).select({ id: feedIdsSub.id }).from(feedIdsSub);

  await db
    .update(feed)
    .set({ loadedFirstPage: false })
    .where(inArray(feed.id, feedIdsSubquery))
    .execute({ feedIds: makePgArray(feedIds) });

  log.debug(
    `[${label}] Complete: ${itemIds.length} items, ${orphanedContentIds.length} content records, ${feedIds.length} feeds`,
  );

  emitTableUpdates(["item", "feed"]);

  return {
    deletedItems: itemIds.length,
    deletedContentIds: orphanedContentIds.length,
    affectedFeeds: feedIds.length,
  };
}
